/** Small accessibility helpers shared by the UI primitives. */

/** Joins element ids for aria-describedby. Returns undefined when no id is left. */
export function describedBy(...ids: Array<string | false | null | undefined>): string | undefined {
  const joined = ids
    .flatMap((id) => (typeof id === 'string' ? id.split(/\s+/) : []))
    .filter((id, index, all) => id.length > 0 && all.indexOf(id) === index)
    .join(' ')
  return joined.length > 0 ? joined : undefined
}

/**
 * Decides where Tab goes inside a focus trap.
 * - Returns an index to move focus to that element.
 * - Returns 'container' when nothing inside can take focus.
 * - Returns null when the browser can move focus by itself.
 * `current` is the index of the focused element, or -1 when focus is outside the list.
 */
export function trapTarget(current: number, count: number, backwards: boolean): number | 'container' | null {
  if (count === 0) return 'container'
  if (backwards) return current <= 0 ? count - 1 : null
  return current === -1 || current === count - 1 ? 0 : null
}

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[contenteditable="true"]',
  '[tabindex]:not([tabindex="-1"])',
].join(',')

/** Visible elements inside `root` that take focus with the Tab key, in document order. */
export function getFocusable(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (element) => element.tabIndex >= 0 && element.getClientRects().length > 0 && !element.closest('[inert]'),
  )
}
